import { DivergingBar, PlotDotLegend } from '@digital-twins/deck-ui/components'
import { AnalystChartSpec } from '@digital-twins/shared-types'
import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { AnalystChart } from '@/features/studies/report/page/components/tabs/AnalystTab/AnalystChart'

import { mapScaleDistribution } from './featuredPlotMappers'
import { routeAnalystChart } from './featuredPlotRouting'

interface FeaturedAnalystChartProps {
  chart: AnalystChartSpec
  index: number
}

export function FeaturedAnalystChart(props: FeaturedAnalystChartProps) {
  const { chart, index } = props
  const { t } = useTranslation('app')
  const decision = routeAnalystChart(chart)

  const titleBlock = (
    <figcaption className="flex flex-col gap-1">
      <Text size="sm">{chart.title}</Text>
      {chart.subtitle ? (
        <Text size="xs" color="muted">
          {chart.subtitle}
        </Text>
      ) : null}
    </figcaption>
  )

  if (decision.renderer === 'text') {
    return (
      <div
        className="rounded-xl border border-border bg-muted/40 p-4"
        data-testid={`thread-featured-plot-invalid-${index}`}
      >
        <Text size="sm" color="muted">
          {t('studyReport.analyst.chartInvalid')}
        </Text>
      </div>
    )
  }

  if (decision.renderer === 'diverging_stacked') {
    const rows = mapScaleDistribution(chart)
    if (!rows) {
      return (
        <div data-testid={`thread-featured-plot-analyst-${index}`}>
          <AnalystChart chart={chart} index={index} />
        </div>
      )
    }
    const legendItems = rows[0].bands
      .filter((band) => band.widthPct > 0 && band.label)
      .map((band) => ({ color: band.color, label: band.label }))

    return (
      <figure
        className="rounded-xl border border-border bg-card p-4"
        data-testid={`thread-featured-plot-${index}`}
      >
        {titleBlock}
        <div className="mt-4 flex flex-col gap-3" data-testid={`thread-featured-plot-diverging-${index}`}>
          {rows.map((row) => (
            <div key={row.id} className="flex flex-col gap-1">
              {rows.length > 1 ? (
                <Text size="xs" color="muted">
                  {row.label}
                </Text>
              ) : null}
              <div className="relative h-8">
                <DivergingBar
                  className="h-8"
                  segments={row.bands.map((band, bandIndex) => ({
                    leftPct: band.leftPct,
                    widthPct: band.widthPct,
                    color: band.color,
                    value: row.captions[bandIndex],
                    label: band.label,
                    'data-testid': `thread-featured-plot-diverging-band-${index}-${band.index}`,
                  }))}
                  ariaLabel={`${row.label}: ${row.captions.filter(Boolean).join(', ')}`}
                  zeroLineClassName="hidden"
                  data-testid={`thread-featured-plot-diverging-bar-${index}-${row.id}`}
                />
              </div>
            </div>
          ))}
          {legendItems.length > 0 ? (
            <PlotDotLegend
              items={legendItems}
              className="text-xs text-muted-foreground"
              data-testid={`thread-featured-plot-diverging-legend-${index}`}
            />
          ) : null}
        </div>
      </figure>
    )
  }

  // Python's plot, a crosstab (grouped bars) and a dense cut (heat grid)
  // all draw through the analyst chart: Python's plot, else the stored read.
  return (
    <div data-testid={`thread-featured-plot-analyst-${index}`}>
      <AnalystChart chart={chart} index={index} />
    </div>
  )
}
